import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './EventPhotos.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://live-event-photos.onrender.com/api';

function CameraRegistration() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [credentials, setCredentials] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cameraInfo: ''
  });

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API_URL}/events/${eventId}`);
      setEvent(response.data);
    } catch (err) {
      console.error('Failed to load event:', err);
      setError('Event not found');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/events/${eventId}/register-camera`,
        formData
      );

      setCredentials(response.data.credentials);
      setSuccess(true);
    } catch (err) {
      console.error('Registration failed:', err);
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="events-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
          <h2 style={{ color: '#EF4444', marginBottom: '16px' }}>❌ Error</h2>
          <p style={{ color: 'var(--text-secondary)' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (success && credentials) {
    return (
      <div className="events-container" style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ fontSize: '32px', marginBottom: '8px', color: '#10B981' }}>✅ Registration Successful!</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '18px' }}>
            {event?.name}
          </p>
        </div>

        <div style={{ 
          background: 'var(--card-bg)', 
          borderRadius: '16px', 
          padding: '24px',
          border: '2px solid #10B981',
          marginBottom: '24px'
        }}>
          <h2 style={{ fontSize: '20px', marginBottom: '16px', color: 'var(--text)' }}>
            📷 FTP Configuration
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '20px', fontSize: '14px' }}>
            Configure your camera with these FTP settings:
          </p>

          <div style={{ display: 'grid', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                FTP Server
              </label>
              <input
                type="text"
                value={credentials.ftpServer}
                readOnly
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  background: 'var(--background)',
                  color: 'var(--text)',
                  fontFamily: 'monospace',
                  fontSize: '14px'
                }}
                onClick={(e) => e.target.select()}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Port
              </label>
              <input
                type="text"
                value={credentials.ftpPort}
                readOnly
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  background: 'var(--background)',
                  color: 'var(--text)',
                  fontFamily: 'monospace',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Username
              </label>
              <input
                type="text"
                value={credentials.ftpUsername}
                readOnly
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  background: 'var(--background)',
                  color: 'var(--text)',
                  fontFamily: 'monospace',
                  fontSize: '14px'
                }}
                onClick={(e) => e.target.select()}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Password
              </label>
              <input
                type="text"
                value={credentials.ftpPassword}
                readOnly
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  background: 'var(--background)',
                  color: 'var(--text)',
                  fontFamily: 'monospace',
                  fontSize: '14px'
                }}
                onClick={(e) => e.target.select()}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Upload Path
              </label>
              <input
                type="text"
                value={credentials.uploadPath}
                readOnly
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  background: 'var(--background)',
                  color: 'var(--text)',
                  fontFamily: 'monospace',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
        </div>

        <div style={{ 
          background: '#FEF3C7', 
          borderRadius: '12px', 
          padding: '16px',
          border: '1px solid #FCD34D'
        }}>
          <p style={{ color: '#92400E', fontSize: '14px', margin: 0 }}>
            💡 <strong>Tip:</strong> Screenshot or write down these credentials. You'll need them to configure your camera's FTP settings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="events-container" style={{ padding: '20px', maxWidth: '500px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <h1 style={{ fontSize: '32px', marginBottom: '8px' }}>📷 Camera Registration</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '18px' }}>
          {event?.name}
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginTop: '8px' }}>
          Register to get your FTP credentials for automatic photo uploads
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '20px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Your Name *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="John Doe"
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              fontSize: '16px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Phone Number *
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            placeholder="(555) 123-4567"
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              fontSize: '16px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Email (Optional)
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john@example.com"
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              fontSize: '16px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Camera Info (Optional)
          </label>
          <input
            type="text"
            name="cameraInfo"
            value={formData.cameraInfo}
            onChange={handleChange}
            placeholder="Canon EOS R5, Sony A7III, etc."
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              fontSize: '16px'
            }}
          />
        </div>

        {error && (
          <div style={{ 
            background: '#FEE2E2', 
            color: '#991B1B', 
            padding: '12px', 
            borderRadius: '8px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          style={{
            width: '100%',
            padding: '16px',
            borderRadius: '8px',
            border: 'none',
            background: submitting ? '#9CA3AF' : '#10B981',
            color: 'white',
            fontSize: '16px',
            fontWeight: '600',
            cursor: submitting ? 'not-allowed' : 'pointer'
          }}
        >
          {submitting ? 'Registering...' : 'Get FTP Credentials'}
        </button>
      </form>
    </div>
  );
}

export default CameraRegistration;
